import Modal from "components/DesignSystem/Modal/Modal";
import { Formik, FormikValues } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  useCreatePersonaWithRolesMutation,
  useGetEntityAPDetailsQuery,
  useGetPeopleForEntityQuery,
  useGetPersonaTypesQuery,
  useUpdateAPAutoFillMutation,
  useUpdateEntityAPDetailsMutation,
} from "store/apis/directorsAndOfficers";
import { RoleBlockPayload } from "store/apis/directorsAndOfficers";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import {
  useGetAllShareholdersQuery,
  useUpdateShareholderMutation,
} from "store/apis/Shareholders";
import { Person } from "types/Models/apAutofills";
import { Shareholder } from "types/Models/Shareholders";
import { BackendError } from "types/utils/error";
import { AddShareholder, COMPANY_ENTITY } from "../Shareholders/AddShareholder";
import { mergePersonAndShareholder } from "./personDetailsUtils";
import { SelectPersonOrEntityType } from "./SelectPersonOrEntityType";
import { AddPersonFormBody } from "./AddPersonFormBody";
import {
  PERSONA_TYPE_DIRECTOR,
  PERSONA_TYPE_OFFICER,
  PERSONA_TYPE_SHAREHOLDER,
  SHAREHOLDER_TYPE_PERSONA,
} from "constants/personaRole";
import {
  blankPersonFormValues,
  buildAddPersonValidationSchema,
  buildEditInitialValues,
  buildRolesForCreate,
  OtherRoleEntry,
  partitionRoleOperationsForEdit,
  STEP_ADD_PERSON,
  STEP_SELECT_TYPE,
  Step,
} from "./addPersonModal.utils";

export const AddPersonModal = ({
  isOpen,
  close,
  personToEdit,
  shareholderToEdit,
  restrictToPersonOnly,
}: {
  isOpen: boolean;
  close: () => void;
  personToEdit?: Person | null;
  shareholderToEdit?: Shareholder | null;
  restrictToPersonOnly?: boolean;
}) => {
  const { entityId } = useParams<{ entityId: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const addEntityShareholderModal = useModal();

  const [activePerson, setActivePerson] = useState<Person | null>(
    personToEdit || null
  );
  const [activeShareholder, setActiveShareholder] =
    useState<Shareholder | null>(shareholderToEdit || null);
  const isEditMode = Boolean(activePerson);
  const [selectedPersonName, setSelectedPersonName] = useState(
    personToEdit
      ? `${personToEdit.first_name} ${personToEdit.last_name}`.trim()
      : ""
  );
  const [showSignatoryWarning, setShowSignatoryWarning] = useState(false);

  const [step, setStep] = useState<Step>(
    personToEdit || restrictToPersonOnly ? STEP_ADD_PERSON : STEP_SELECT_TYPE
  );
  const [createPersonaWithRoles] = useCreatePersonaWithRolesMutation();
  const [updateAPAutofill] = useUpdateAPAutoFillMutation();
  const [updateShareholderAutofill] = useUpdateShareholderMutation();
  const { data: personaTypes = [] } = useGetPersonaTypesQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: countries } = useGetCountriesDataQuery();
  const { data: entityAPDetails } = useGetEntityAPDetailsQuery(
    { entityId },
    { skip: !entityId }
  );
  const [updateEntityAPDetails] = useUpdateEntityAPDetailsMutation();
  const { data: peopleData } = useGetPeopleForEntityQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: shareholders = [] } = useGetAllShareholdersQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId || Boolean(personToEdit) }
  );

  const handleClose = () => {
    setStep(
      personToEdit || restrictToPersonOnly ? STEP_ADD_PERSON : STEP_SELECT_TYPE
    );
    close();
  };

  const findExistingPerson = (personaAutofillId: string) =>
    [...(peopleData?.people || []), ...(peopleData?.former_people || [])].find(
      (person) => person.persona_autofill_id === personaAutofillId
    );

  const findExistingShareholder = (personaAutofillId: string) =>
    shareholders.find(
      (shareholder) =>
        shareholder.shareholder_type === SHAREHOLDER_TYPE_PERSONA &&
        shareholder.instance_id === personaAutofillId
    );

  const formInitialValues = activePerson
    ? buildEditInitialValues(activePerson, activeShareholder)
    : blankPersonFormValues;

  const handlePersonaChange = (
    personaAutofillId: string,
    fallbackName?: string
  ): typeof blankPersonFormValues => {
    const existingPerson = findExistingPerson(personaAutofillId);
    const existingShareholder = findExistingShareholder(personaAutofillId);
    const mergedPerson = mergePersonAndShareholder(
      existingPerson,
      existingShareholder
    );

    if (mergedPerson) {
      setActivePerson(mergedPerson);
      setActiveShareholder(existingShareholder || null);
      setSelectedPersonName(
        `${mergedPerson.first_name} ${mergedPerson.last_name}`.trim()
      );
      return buildEditInitialValues(mergedPerson, existingShareholder);
    }

    setActivePerson(null);
    setActiveShareholder(null);
    setSelectedPersonName(fallbackName || "");
    return { ...blankPersonFormValues, personaAutofillId };
  };

  const updateEntityAPDetailsForNewRoles = async (
    roles: RoleBlockPayload[]
  ) => {
    if (!entityAPDetails) return;
    const addedDirector = roles.some(
      (role) => role.role_type === PERSONA_TYPE_DIRECTOR
    );
    const addedOfficer = roles.some(
      (role) => role.role_type === PERSONA_TYPE_OFFICER
    );
    const addedShareholder = roles.some(
      (role) => role.role_type === PERSONA_TYPE_SHAREHOLDER
    );
    await updateEntityAPDetails({
      payload: {
        ...entityAPDetails,
        director_exists: addedDirector ? true : entityAPDetails.director_exists,
        officer_exists: addedOfficer ? true : entityAPDetails.officer_exists,
        shareholder_exists: addedShareholder
          ? true
          : entityAPDetails.shareholder_exists,
      },
      entityId,
    }).unwrap();
  };

  const handleSubmit = async (values: FormikValues) => {
    const roles = buildRolesForCreate(values, personaTypes);

    try {
      await createPersonaWithRoles({
        groupId,
        entityId,
        payload: { persona_autofill_id: values.personaAutofillId, roles },
      }).unwrap();

      await updateEntityAPDetailsForNewRoles(roles);

      successToast({ message: "Person added successfully" });
      handleClose();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleEditSubmit = async (values: FormikValues) => {
    try {
      const { updates: ops, newRoles } = partitionRoleOperationsForEdit(
        values,
        personaTypes,
        activeShareholder
      );

      const updates: Promise<unknown>[] = ops.map((op) =>
        op.kind === "AP_UPDATE"
          ? updateAPAutofill({
              entityId,
              personaType: op.personaType,
              personaId: op.joinId,
              payload: op.payload,
            }).unwrap()
          : updateShareholderAutofill({
              groupId,
              shareholderId: op.shareholderId,
              payload: op.payload,
            }).unwrap()
      );

      if (newRoles.length) {
        updates.push(
          createPersonaWithRoles({
            groupId,
            entityId,
            payload: {
              persona_autofill_id: values.personaAutofillId,
              roles: newRoles,
            },
          }).unwrap()
        );
        updates.push(updateEntityAPDetailsForNewRoles(newRoles));
      }

      await Promise.all(updates);
      successToast({ message: "Person updated successfully" });
      handleClose();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleAddEntityShareholderClose = () => {
    addEntityShareholderModal.close();
    handleClose();
  };

  if (addEntityShareholderModal.isOpen) {
    return (
      <AddShareholder
        isOpen={addEntityShareholderModal.isOpen}
        close={handleAddEntityShareholderClose}
        entityId={entityId}
        lockedShareholderType={COMPANY_ENTITY}
      />
    );
  }

  const isOtherPillLocked =
    isEditMode &&
    (formInitialValues.otherRoles as OtherRoleEntry[]).some(
      (entry) => entry.joinId
    );
  const isShareholderLocked = isEditMode && Boolean(activeShareholder);

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose} modal={false}>
      <Modal.Content useCustomOverlay>
        {step === STEP_SELECT_TYPE && (
          <SelectPersonOrEntityType
            onClose={handleClose}
            onSelectPerson={() => setStep(STEP_ADD_PERSON)}
            onSelectEntity={addEntityShareholderModal.open}
          />
        )}

        {step === STEP_ADD_PERSON && (
          <Formik
            initialValues={formInitialValues}
            validationSchema={buildAddPersonValidationSchema(personaTypes)}
            validateOnMount
            onSubmit={isEditMode ? handleEditSubmit : handleSubmit}
          >
            <AddPersonFormBody
              entityId={entityId}
              personToEdit={personToEdit}
              activePerson={activePerson}
              personaTypes={personaTypes}
              countries={countries}
              peopleData={peopleData}
              isEditMode={isEditMode}
              isShareholderLocked={isShareholderLocked}
              isOtherPillLocked={isOtherPillLocked}
              selectedPersonName={selectedPersonName}
              showSignatoryWarning={showSignatoryWarning}
              onShowSignatoryWarningChange={setShowSignatoryWarning}
              onPersonaChange={handlePersonaChange}
              onClose={handleClose}
            />
          </Formik>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
